import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import {Button, COLOR} from '@dized/ui';

export const GridButton = styled(Button)`
  margin: 0 5px;
  min-width: fit-content;
`;

const Container = styled.div`
  display: grid;
  grid-template-columns: ${props => 
    props.columns.map(col => col.template || 'max-content').join(' ')
};
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e0e6ed;
  position: relative;
`;

const HeaderCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 12px 16px;
  font-weight: 600;
  color: #6c757d;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
`;

const DataCell = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  color: #495057;
  font-size: 16px;
  line-height: 1.4;
  font-weight: 500;
  border-bottom: 1px solid #f0f3f6;
  transition: all 0.2s ease;
`;

const DataRow = styled.div`
  display: contents;
  
  &:nth-child(even) ${DataCell} {
    background-color: rgba(248, 249, 250, 0.3);
  }
  
  &:hover ${DataCell} {
    background-color: #f8f9fa;
  }
  
  &:last-child ${DataCell} {
    border-bottom: none;
  }
`;

const ClickableDataRow = styled(DataRow)`
  cursor: pointer;
  
  &:hover ${DataCell} {
    background-color: #e3f2fd;
    transform: translateY(-1px);
  }
`;

const HeaderRow = styled.div`
  display: contents;
`;

export const HeaderActions = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
  padding: 0;
  margin: -4px 0;
`;

// Styled button that matches DropDownMenu button exactly
export const AddButton = styled.div`
  width: 22px;
  height: 22px;
  padding: 4px;
  border-radius: 4px;
  background-color: ${props => props.disabled ? COLOR.DISABLED_GRAY : COLOR.TURQUOISE};
  cursor: ${props => props.disabled ? 'default' : 'pointer'};
  display: flex;
  align-items: center;
  justify-content: center;
  
  .material-icons {
    color: ${COLOR.LIGHT_GRAY};
    font-size: 16px;
    line-height: 1;
    user-select: none;
  }
`;

/**
 * Custom data grid component with full styling control
 * @param {Object} props - Component props
 * @returns {React.Component} The DataGrid component
 */
export const DataGrid = ({
  columns,
  data,
  className,
  onRowClick,
}) => {
  const RowComponent = onRowClick ? ClickableDataRow : DataRow;
  
  return (
    <Container className={className} columns={columns}>
      <HeaderRow>
        {columns.map((column, index) => (
          <HeaderCell key={index}>
            {column.renderHeader ? column.renderHeader() : column.title}
          </HeaderCell>
        ))}
      </HeaderRow>
      
      {data.map((rowData, rowIndex) => (
        <RowComponent 
          key={rowIndex}
          onClick={onRowClick ? (e) => {
            // Don't trigger row click if the click came from a button or interactive element
            if (e.target.tagName === 'BUTTON' || e.target.closest('button')) {
              return;
            }
            onRowClick(rowData);
          } : undefined}
        >
          {columns.map((column, columnIndex) => (
            <DataCell key={columnIndex}>
              {column.renderCell(rowData)}
            </DataCell>
          ))}
        </RowComponent>
      ))}
    </Container>
  );
};

DataGrid.propTypes = {
  /** Allow styling of component */
  className: PropTypes.string,
  /** Structure of the data */
  columns: PropTypes.arrayOf(PropTypes.shape({
    /** How to render this field */
    renderCell: PropTypes.func.isRequired,
    /** How to render the header (optional, defaults to title) */
    renderHeader: PropTypes.func,
    /** CSS grid column template - default is "max-content" */
    template: PropTypes.string,
    /** Text to be displayed on column header */
    title: PropTypes.string.isRequired
  })).isRequired,
  /** Data to be displayed */
  data: PropTypes.array.isRequired,
  /** Function to call when a row is clicked */
  onRowClick: PropTypes.func,
};

DataGrid.defaultProps = {
  className: '',
  data: [],
};
